import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import { formatKWD, colorHex, colorLabel } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { MEASURE_FIELDS, measureErrorInRange, MEASURE_CONFIRM_TEXT, TERMS_CONFIRM_TEXT, ALTERATION_CONFIRM_TEXT, CHECKOUT_NOTICE, INTL_NOTICE, TEXT_EN, DEFAULT_RANGES, fieldLabel } from "@/lib/measure";
import { KUWAIT_GOVERNORATES, COUNTRY_EN } from "@/lib/kuwaitAreas";

const STEPS = { ar: ["بياناتك", "المقاسات", "المراجعة والإقرار", "الدفع"], en: ["Your details", "Measurements", "Review & confirm", "Payment"] };
const EMPTY_M = { length: "", shoulder: "", chest: "", sleeve: "", hip: "" };

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const { lang, tr } = useLang();
  const en = lang === "en";
  const [p, setP] = useState(null);
  const [ai, setAi] = useState(0);
  const galleryTouch = useRef(null);
  const [settings, setSettings] = useState(null);
  const [step, setStep] = useState(1);
  const [cust, setCust] = useState({ name: "", phone: "", country: "KW", governorate: "", region: "", address: "", city: "", state: "", zip: "" });
  const [m, setM] = useState(EMPTY_M);
  const [notes, setNotes] = useState("");
  const [color, setColor] = useState(null);
  const [ap, setAp] = useState({ measure: false, terms: false, alteration: false });
  const [added, setAdded] = useState(false);
  const configRef = useRef(null);

  const scrollToStep = () => {
    setTimeout(() => configRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => nav("/shop"));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [id]);
  if (!p) return <div className="py-24 text-center text-text-muted">…</div>;

  const steps = STEPS[lang] || STEPS.ar;
  const name = en ? (p.name_en || p.name_ar) : p.name_ar;
  const ranges = { ...DEFAULT_RANGES, ...(settings?.measurement_ranges || {}) };
  const colors = [...new Map((p.variants || []).map((v) => [v.color, v])).values()];
  const soldOut = p.availability === "sold_out";
  const shipCountries = (settings?.shipping_countries || []).filter((c) => c.active !== false);
  const isKW = cust.country === "KW";
  const countryName = (code) => (en ? COUNTRY_EN[code] || code : (shipCountries.find((c) => c.code === code)?.name_ar || code));
  const govName = (g) => (en ? g.en || g.name : g.name);
  const phoneOk = /^\+?\d{8,15}$/.test((cust.phone || "").replace(/\s/g, ""));
  const gov = KUWAIT_GOVERNORATES.find((g) => g.name === cust.governorate);
  const govAreas = gov?.areas || [];
  const step1Ok = cust.name && phoneOk && cust.country && (isKW ? (cust.governorate && cust.region && cust.address) : (cust.city && cust.address));
  const step2Ok = !measureErrorInRange(m, ranges);
  const step3Ok = ap.measure && ap.terms && ap.alteration;
  const knetLabel = tr("كي نت (KNET)", "KNET");

  const next = () => {
    if (step === 1 && !step1Ok) { toast.error(!phoneOk ? tr("أدخلي رقم هاتف صحيح", "Please enter a valid phone number") : tr("يرجى إكمال بيانات التواصل", "Please complete your contact details")); return; }
    if (step === 2 && !step2Ok) { toast.error(measureErrorInRange(m, ranges, lang)); return; }
    if (step === 3 && !step3Ok) { toast.error(tr("يجب الموافقة على جميع الإقرارات للمتابعة", "Please accept all confirmations to continue")); return; }
    setStep((s) => Math.min(4, s + 1));
    scrollToStep();
  };

  const addToCart = () => {
    if (!(step1Ok && step2Ok && step3Ok)) { toast.error(tr("أكملي جميع الخطوات أولاً", "Please complete all steps first")); return; }
    const chosen = color || (colors[0]?.color || "");
    const cv = colors.find((v) => v.color === chosen);
    const measurements = Object.fromEntries(MEASURE_FIELDS.map((f) => [f.key, +m[f.key]]));
    add({
      product_id: p.id, name_ar: p.name_ar, name_en: p.name_en, sku: p.sku, price: p.price, image: p.images?.[0],
      color: chosen, color_ar: cv ? (cv.color_ar || cv.color) : "",
      measurements, notes: notes.trim(),
      customer: { ...cust }, payment_method: "knet",
      approvals: { measure: true, terms: true, alteration: true, terms_version: settings?.policies?.terms?.version || 1, accepted_at: new Date().toISOString() },
    });
    toast.success(tr("تمت إضافة القطعة إلى السلة بنجاح", "Added to your cart"));
    setAdded(true);
  };

  const inputCls = "w-full mt-1 border border-border px-3 py-2.5 text-sm bg-transparent focus:outline-none focus:ring-1 focus:ring-primary";
  const lbl = "text-sm text-text-muted";
  const cm = tr("سم", "cm");

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-6 md:py-12 fade-in">
      <div className="grid md:grid-cols-2 gap-6 md:gap-12">
        {/* Gallery: full original image, no crop */}
        <div className="space-y-3 md:sticky md:top-24 md:self-start">
          <div className="relative aspect-[2/3] overflow-hidden bg-[#F3EFEA]"
            onTouchStart={(e) => { galleryTouch.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              if (galleryTouch.current == null || !p.images?.length) return;
              const dx = e.changedTouches[0].clientX - galleryTouch.current;
              if (Math.abs(dx) > 40) setAi((v) => (v + (dx > 0 ? -1 : 1) + p.images.length) % p.images.length);
              galleryTouch.current = null;
            }}
            data-testid="pd-gallery">
            {p.images?.[ai] && <img src={resolveImg(p.images[ai])} alt={name} data-testid="pd-main-image" className="w-full h-full object-contain" />}
            {p.images?.length > 1 && (
              <>
                <button onClick={() => setAi((v) => (v - 1 + p.images.length) % p.images.length)} data-testid="pd-prev" aria-label={tr("الصورة السابقة", "Previous image")}
                  className="absolute top-1/2 -translate-y-1/2 start-2 w-9 h-9 rounded-full bg-[#F9F6F0]/85 backdrop-blur-sm text-[#141110] flex items-center justify-center hover:bg-[#F9F6F0] transition-colors">
                  {en ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>
                <button onClick={() => setAi((v) => (v + 1) % p.images.length)} data-testid="pd-next" aria-label={tr("الصورة التالية", "Next image")}
                  className="absolute top-1/2 -translate-y-1/2 end-2 w-9 h-9 rounded-full bg-[#F9F6F0]/85 backdrop-blur-sm text-[#141110] flex items-center justify-center hover:bg-[#F9F6F0] transition-colors">
                  {en ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
                </button>
                <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5 md:hidden">
                  {p.images.map((_, i) => (
                    <span key={i} className={`h-1.5 rounded-full transition-all ${ai === i ? "w-5 bg-[#3C0F1A]" : "w-1.5 bg-[#141110]/30"}`} />
                  ))}
                </div>
              </>
            )}
          </div>
          {p.images?.length > 1 && (
            <div className="grid grid-cols-4 gap-2">
              {p.images.map((img, i) => (
                <button key={i} onClick={() => setAi(i)} data-testid={`pd-thumb-${i}`} aria-label={`${tr("صورة", "Image")} ${i + 1}`}
                  className={`aspect-[2/3] bg-[#F3EFEA] overflow-hidden border-2 transition-colors ${ai === i ? "border-[#3C0F1A]" : "border-transparent hover:border-[#B89C72]"}`}>
                  <img src={resolveImg(img)} alt="" loading="lazy" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Config */}
        <div ref={configRef} className="scroll-mt-24">
          <span className="text-xs tracking-luxe uppercase text-accent">{p.sku}</span>
          <h1 className="font-display text-3xl md:text-4xl mt-2 mb-1">{name}</h1>
          <p className="text-xl mb-3" dir="ltr" style={{ textAlign: en ? "left" : "right" }} data-testid="pd-price">{formatKWD(p.price, lang)}</p>
          {soldOut && <div className="inline-block bg-[#141110] text-[#F9F6F0] text-xs px-3 py-1.5 mb-4" data-testid="pd-soldout">{tr("نفد حالياً — غير متاح للطلب", "Currently sold out")}</div>}
          <div className="inline-block bg-surface border border-border text-xs text-text-muted px-3 py-1.5 mb-6">{tr("تُفصّل حسب مقاسك · قابل لطلبات متعددة", "Tailored to your measurements · multiple orders allowed")}</div>

          <div className="flex items-start justify-between mb-6" data-testid="wizard-stepper">
            {steps.map((label, i) => {
              const n = i + 1;
              const done = step > n;
              const active = step === n;
              return (
                <React.Fragment key={i}>
                  <div className="flex flex-col items-center gap-1.5 flex-shrink-0 w-16">
                    <div data-testid={`step-node-${n}`}
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium border transition-colors ${done || active ? "bg-[#171717] text-white border-[#171717]" : "bg-white text-text-muted border-border"}`}>
                      {done ? <Check className="w-4 h-4" strokeWidth={2.5} /> : n}
                    </div>
                    <span className={`text-[10px] text-center leading-tight ${active ? "text-primary font-medium" : "text-text-muted"}`}>{label}</span>
                  </div>
                  {i < steps.length - 1 && <div className={`flex-1 h-0.5 mt-4 ${step > n ? "bg-primary" : "bg-border"}`} />}
                </React.Fragment>
              );
            })}
          </div>
          <p className="text-sm font-medium mb-4" data-testid="step-title">{tr("الخطوة", "Step")} {step}: {steps[step - 1]}</p>

          {step === 1 && (
            <div className="space-y-3" data-testid="step-details">
              <div><label className={lbl}>{tr("الاسم الكامل", "Full name")}</label><input value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} data-testid="cust-name" className={inputCls} /></div>
              <div><label className={lbl}>{tr("رقم الهاتف", "Phone number")}</label><input value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} dir="ltr" data-testid="cust-phone" className={inputCls} />
                {cust.phone && !phoneOk && <p className="text-xs text-destructive mt-1">{tr("رقم الهاتف غير صحيح", "Invalid phone number")}</p>}</div>
              <div><label className={lbl}>{tr("الدولة / الوجهة", "Country / destination")}</label>
                <select value={cust.country} onChange={(e) => setCust({ ...cust, country: e.target.value, governorate: "", region: "", city: "", state: "", zip: "" })} data-testid="cust-country" className={inputCls}>
                  <option value="KW">{tr("توصيل داخل الكويت", "Delivery within Kuwait")}</option>
                  {shipCountries.map((c) => <option key={c.code} value={c.code}>{en ? `International shipping to ${countryName(c.code)}` : `شحن دولي إلى ${c.name_ar}`}</option>)}
                </select></div>
              {isKW ? (
                <>
                  <div><label className={lbl}>{tr("المحافظة", "Governorate")}</label>
                    <select value={cust.governorate} onChange={(e) => setCust({ ...cust, governorate: e.target.value, region: "" })} data-testid="cust-governorate" className={inputCls}>
                      <option value="">{tr("— اختاري المحافظة —", "— Select governorate —")}</option>
                      {KUWAIT_GOVERNORATES.map((g) => <option key={g.name} value={g.name}>{govName(g)}</option>)}
                    </select></div>
                  <div><label className={lbl}>{tr("المنطقة", "Area")}</label>
                    <select value={cust.region} onChange={(e) => setCust({ ...cust, region: e.target.value })} data-testid="cust-region" disabled={!cust.governorate} className={inputCls}>
                      <option value="">{cust.governorate ? tr("— اختاري المنطقة —", "— Select area —") : tr("اختاري المحافظة أولاً", "Select a governorate first")}</option>
                      {govAreas.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select></div>
                  <div><label className={lbl}>{tr("العنوان بالتفصيل", "Detailed address")}</label><textarea value={cust.address} onChange={(e) => setCust({ ...cust, address: e.target.value })} rows={2} data-testid="cust-address" className={inputCls} /></div>
                </>
              ) : (
                <>
                  <div><label className={lbl}>{tr("المدينة", "City")}</label><input value={cust.city} onChange={(e) => setCust({ ...cust, city: e.target.value })} data-testid="cust-city" className={inputCls} /></div>
                  <div><label className={lbl}>{tr("المنطقة / الولاية", "Area / state")}</label><input value={cust.state} onChange={(e) => setCust({ ...cust, state: e.target.value, region: e.target.value })} data-testid="cust-state" className={inputCls} /></div>
                  <div><label className={lbl}>{tr("العنوان بالتفصيل", "Detailed address")}</label><textarea value={cust.address} onChange={(e) => setCust({ ...cust, address: e.target.value })} rows={2} data-testid="cust-address" className={inputCls} /></div>
                  <div><label className={lbl}>{tr("الرمز البريدي (إن وجد)", "Postal code (optional)")}</label><input value={cust.zip} onChange={(e) => setCust({ ...cust, zip: e.target.value })} dir="ltr" data-testid="cust-zip" className={inputCls} /></div>
                </>
              )}
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-measure">
              {colors.length > 1 && (
                <div className="mb-4"><div className="text-sm mb-2">{tr("اللون", "Color")}</div>
                  <div className="flex gap-3">{colors.map((v) => (
                    <button key={v.color} onClick={() => setColor(v.color)} data-testid={`color-${v.color}`} title={colorLabel(v, lang)}
                      className={`w-9 h-9 rounded-full border-2 ${color === v.color ? "border-primary scale-110" : "border-border"}`} style={{ background: colorHex(v.color) }} />
                  ))}</div></div>
              )}
              <p className="text-xs text-text-muted mb-3">{tr("جميع القياسات بالسنتيمتر (سم) — يمكنك إدخال نصف سنتيمتر مثل 94.5. أدخلي قيمة ضمن النطاق الموضّح بجانب كل خانة.", "All measurements are in centimetres (cm) — half values like 94.5 are accepted. Enter a value within the range shown next to each field.")}</p>
              <div className="grid grid-cols-1 gap-3">
                {MEASURE_FIELDS.map((fld) => (
                  <div key={fld.key}>
                    <label className="text-sm">{fieldLabel(fld, lang)} <span className="text-[10px] text-text-muted">({ranges[fld.key].min}-{ranges[fld.key].max} {cm})</span></label>
                    <input type="number" min={ranges[fld.key].min} max={ranges[fld.key].max} step="0.5" inputMode="decimal" value={m[fld.key]} onChange={(e) => setM({ ...m, [fld.key]: e.target.value })} data-testid={`measure-${fld.key}`} className={inputCls} />
                    <p className="text-[10px] text-text-muted mt-0.5">{en ? fld.hint_en : fld.hint}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <label className={lbl}>{tr("ملاحظات إضافية (اختياري)", "Additional notes (optional)")}</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500} data-testid="measure-notes" placeholder={tr("أي تفاصيل تودّين إضافتها عن التفصيل…", "Any tailoring details you'd like to add…")} className={inputCls} />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="step-review">
              <div className="bg-surface border border-border p-4 text-sm space-y-1">
                <div className="flex justify-between"><span>{name} · {p.sku}</span><span dir="ltr">{formatKWD(p.price, lang)}</span></div>
                <div className="text-text-muted text-xs">{cust.name} · <span dir="ltr">{cust.phone}</span></div>
                <div className="text-text-muted text-xs">{isKW ? `${tr("الكويت", "Kuwait")} / ${gov ? govName(gov) : ""} / ${cust.region}` : `${countryName(cust.country)} / ${cust.city}${cust.region ? " / " + cust.region : ""}`} — {cust.address}</div>
                <div className="text-text-muted text-xs">{tr("المقاسات", "Measurements")}: {MEASURE_FIELDS.map((fld) => `${fieldLabel(fld, lang)} ${m[fld.key]}`).join(" · ")} {cm}</div>
                {notes.trim() && <div className="text-text-muted text-xs">{tr("ملاحظات", "Notes")}: {notes.trim()}</div>}
                <div className="text-text-muted text-xs">{tr("طريقة الدفع", "Payment method")}: {knetLabel}</div>
              </div>
              <p className="text-xs leading-relaxed bg-surface border border-border p-3" data-testid="notice">{isKW ? tr(CHECKOUT_NOTICE, TEXT_EN.notice) : tr(INTL_NOTICE, TEXT_EN.intl)}</p>
              {!en && <div className="text-[11px] leading-relaxed bg-surface border border-border p-3 text-text-muted" data-testid="alteration-policy-text">{settings?.policies?.alteration?.content}</div>}
              <label className="flex items-start gap-2 text-sm cursor-pointer"><input type="checkbox" checked={ap.measure} onChange={(e) => setAp({ ...ap, measure: e.target.checked })} data-testid="approve-measure" className="mt-1" /><span>{tr(MEASURE_CONFIRM_TEXT, TEXT_EN.measure)}</span></label>
              <label className="flex items-start gap-2 text-sm cursor-pointer"><input type="checkbox" checked={ap.terms} onChange={(e) => setAp({ ...ap, terms: e.target.checked })} data-testid="approve-terms" className="mt-1" /><span>{tr(TERMS_CONFIRM_TEXT, TEXT_EN.terms)} (<Link to="/policies" target="_blank" className="underline text-primary">{tr("عرض", "View")}</Link>)</span></label>
              <label className="flex items-start gap-2 text-sm cursor-pointer"><input type="checkbox" checked={ap.alteration} onChange={(e) => setAp({ ...ap, alteration: e.target.checked })} data-testid="approve-alteration" className="mt-1" /><span>{tr(ALTERATION_CONFIRM_TEXT, TEXT_EN.alteration)}</span></label>
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-payment">
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="pay-knet" className="min-h-[44px] py-3 text-sm border bg-[#171717] text-white border-[#171717]">{knetLabel}</button>
              </div>
              <p className="text-xs text-accent mt-3">{tr("الدفع عبر كي نت (KNET) آمن؛ لن يبدأ التفصيل قبل تأكيد نجاح الدفع.", "KNET payment is secure; tailoring starts only after payment is confirmed.")}</p>
            </div>
          )}

          <div className="flex gap-3 mt-6">
            {step > 1 && <button onClick={() => { setStep((s) => s - 1); scrollToStep(); }} data-testid="step-back" className="btn-secondary px-6 py-3 text-sm">{tr("السابق", "Back")}</button>}
            {step < 4 ? (
              <button onClick={next} data-testid="step-next" className="btn-primary flex-1 py-3 text-sm tracking-wide">
                {step === 1 ? tr("التالي: إدخال المقاسات", "Next: enter measurements") : tr("التالي", "Next")}
              </button>
            ) : (
              <button onClick={addToCart} disabled={soldOut} data-testid="add-to-cart-btn" className="btn-primary flex-1 py-3 text-sm tracking-wide disabled:opacity-40">{soldOut ? tr("نفد حالياً", "Sold out") : tr("إضافة إلى السلة", "Add to cart")}</button>
            )}
          </div>
          {added && step === 4 && (
            <div className="mt-3 bg-[#F1EADD] border border-[#B89C72]/40 p-3 text-center space-y-2" data-testid="cart-success">
              <p className="text-sm text-[#141110]">{tr("تمت إضافة القطعة إلى سلتك ✓", "Added to your cart ✓")}</p>
              <Link to="/cart" data-testid="view-cart-btn" className="btn-primary inline-block px-6 py-2.5 text-sm">{tr("عرض السلة وإتمام الطلب", "View cart & checkout")}</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
